import * as React from 'react';

import { IconButton, Select, TextField } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtomValue, useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import { NumericInputCell } from '../NumericInputCell';

import { AddNotesButton } from './AddNotesButton';
import { gridLayoutFreight } from './constants/gridLayout';
import { FreightChargeDialog } from './FreightChargeDialog';

import {
  $freightForwarders,
  $freightLines,
  $removeFreightCharge,
  $updateFreightCharge,
  FreightChargeForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { addMoney } from '~/modules/trade/utils/money';

interface FreightChargeLineItemProps {
  freightCharge: FreightChargeForm;
  dialogMode?: boolean;
  disabled?: boolean;
}

export const FreightChargeLineItem = ({
  freightCharge,
  dialogMode = true,
  disabled = false,
}: FreightChargeLineItemProps) => {
  const t = useTranslations();
  const { data: freightForwardersData } = useAtomValue($freightForwarders);
  const { data: freightLinesData } = useAtomValue($freightLines);
  const updateFreightCharge = useSetAtom($updateFreightCharge);
  const removeFreightCharge = useSetAtom($removeFreightCharge);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const haulerOptions = React.useMemo(
    () =>
      freightForwardersData?.freightForwarders.map((ff) => ({
        key: ff.id,
        label: ff.name,
      })) ?? [],
    [freightForwardersData],
  );

  const freightLaneOptions = React.useMemo(
    () =>
      freightLinesData?.freightLines.map((fl) => ({
        key: fl.id,
        label: fl.name,
      })) ?? [],
    [freightLinesData],
  );

  const handleFieldChange = (
    field: keyof Omit<FreightChargeForm, 'id'>,
    value?: string | number,
  ) => {
    if (field === 'accruedAmount' || field === 'chargebackAmount') {
      updateFreightCharge(freightCharge.id, {
        [field]: {
          ...freightCharge[field],
          amount:
            typeof value === 'string' && !!value
              ? parseFloat(value)
              : value || 0,
        },
      });
    } else {
      updateFreightCharge(freightCharge.id, { [field]: value });
    }
  };

  const netAmount = addMoney(
    freightCharge.accruedAmount,
    freightCharge.chargebackAmount,
  );

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <div className="@FreightChargeLineItem contents">
      <div className={cn('grid w-full items-center gap-2', gridLayoutFreight)}>
        <Select
          value={freightCharge.hauler}
          onChange={(value) => handleFieldChange('hauler', value)}
          options={haulerOptions}
          placeholder={
            t.materialsReconciliation.freightCharge.placeholders.selectHauler
          }
          isDisabled={disabled}
        />
        <Select
          value={freightCharge.freightLane}
          onChange={(value) => handleFieldChange('freightLane', value)}
          options={freightLaneOptions}
          placeholder={
            t.materialsReconciliation.freightCharge.placeholders.selectLane
          }
          isDisabled={disabled}
        />
        {/* Column 3: Delivery Number */}
        <div>
          <TextField
            value={freightCharge.deliveryNumber}
            onChange={(value) => handleFieldChange('deliveryNumber', value)}
            innerLabel={
              t.materialsReconciliation.freightCharge.fields.deliveryNumber
            }
            className={cn(
              '!*:border-neutral/20 *:!h-[40px]',
              disabled &&
                'cursor-not-allowed !bg-neutral/5 *:cursor-not-allowed',
            )}
            isDisabled={disabled}
          />
        </div>
        {/* Column 4: Accrued Amount */}
        <NumericInputCell
          value={freightCharge.accruedAmount.amount}
          onChange={(v) => handleFieldChange('accruedAmount', v)}
          innerLabel={t.materialsReconciliation.freightCharge.fields.accrued}
          prefix="$"
          isDisabled={disabled}
        />
        {/* Column 5: Chargeback Amount */}
        <NumericInputCell
          value={freightCharge.chargebackAmount.amount}
          onChange={(v) => handleFieldChange('chargebackAmount', v)}
          innerLabel={t.materialsReconciliation.freightCharge.fields.chargeback}
          prefix="$"
          isDisabled={disabled}
        />
        {/* Column 6: Total Freight Amount */}
        <NumericInputCell
          className="!*:bg-failure/20 !*:text-failure"
          value={netAmount.amount}
          isDisabled
          prefix="$"
        />
        {/* Column 7: Notes Button */}
        <AddNotesButton
          isDisabled={disabled}
          initialValue={freightCharge.notes}
          onAddNotes={(notes) => handleFieldChange('notes', notes)}
        />
        {/* Column 8: Delete Item */}
        <IconButton
          icon={<Trash2 size={18} />}
          variant="outline"
          className="size-[40px]"
          color="failure"
          aria-label={t.materialsReconciliation.freightCharge.actions.delete}
          onPress={() => removeFreightCharge(freightCharge.id)}
          isDisabled={disabled}
        />
      </div>

      {/* Dialog for non-dialog mode */}
      {!dialogMode && (
        <FreightChargeDialog
          freightCharge={freightCharge}
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          disabled={disabled}
        />
      )}
    </div>
  );
};
